import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Row, Col, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { batchAddToCart, fetchCartProducts } from 'store/slices/cartSlice';
import { fetchHistory } from 'store/slices/customerSlice';
import AccountMenu from 'components/menu/AccountMenu';
import AccountLogoutButton from 'components/menu/AccountMenu/AccountLogoutButton';
import CartQtyButtonGroup from 'components/cart/CartQtyButtonGroup';
import WishlistButton from 'components/cart/WishlistButton';
import useSmartNavigate from 'hooks/useSmartNavigate';
import { formatCurrency, formatWeightWithUnit } from 'helpers/formatter';
import { loadingStatus } from 'helpers/fetcher';
import { getStock } from 'helpers/product';
import ProductPriceRow from 'components/product/ProductPriceRow';
import Icon from 'components/Icon';

const HistoryDetail = () => {
  const { t } = useTranslation();
  const [reorderIsLoad, setReorderIsload] = useState();
  const dispatch = useDispatch();
	const navigate = useNavigate();
	const { navigate: smartNavigate } = useSmartNavigate();
	const { data, status } = useSelector(state => state.customer.history);
	const { '*': orderId } = useParams();

  const handleBatchAdd = async () => {
    setReorderIsload(true);
    const products = data.products
			.filter(p => p.reorder)
      .map(({ product_id, quantity }) => ({ product_id, quantity }));
    await batchAddToCart(products);
    dispatch(fetchCartProducts({ refetchAfterMutation: true }));
    setReorderIsload(false);
  };

  useEffect(() => {
    dispatch(fetchHistory(orderId));
  }, [dispatch, orderId]);

  return (
		<div className="region account-section">
			{/* Breadcrumb */}
			<div className="contact-breadcrumb" style={{ marginTop: '64px' }}>
				<a
					href="/"
					onClick={e => {
						e.preventDefault();
						navigate('/');
					}}
					className="breadcrumb-link"
				>
				{t('common.home')}
			</a>
			<span className="breadcrumb-separator breadcrumb-separator-active"></span>
			<span className="breadcrumb-current">{t('account.personalAccount')}</span>
		</div>

		{/* Title */}
		<h1 className="account-title">{t('account.orderPrefix', { id: orderId })}</h1>

			{/* Layout */}
			<div className="account-layout">
				{/* Sidebar */}
				<div className="account-sidebar">
					<AccountMenu />
				</div>

				{/* Content */}
				<div className="account-content">
            {status === loadingStatus.SUCCEEDED ? (
              <>
							<div className="account-order-detail-card">
								<div className="account-order-detail-grid">
									{/* Order Details */}
									<div className="account-order-detail-section">
									<h3 className="account-order-detail-title">
										{t('account.orderDetails')}
									</h3>
									<p className="account-order-detail-label">{t('account.orderNumber')}</p>
									<p className="account-order-detail-value">{data.order_id}</p>
									<p className="account-order-detail-label">{t('account.dateAdded')}</p>
										<p className="account-order-detail-value">
											{data.date_added}
										</p>
										<p className="account-order-detail-label">{t('account.paymentMethod')}</p>
										<p className="account-order-detail-value">
											{data.payment_method}
										</p>
									<p className="account-order-detail-label">
										{t('account.deliveryMethod')}
									</p>
										<p className="account-order-detail-value">
											{data.shipping_method}
										</p>
                <Button
                  type="primary"
											className="account-order-detail-button"
                  onClick={handleBatchAdd}
                  loading={reorderIsLoad}
                >
											{t('account.reorder')}
                </Button>
									</div>

									{/* Shipping Address */}
									<div className="account-order-detail-section">
									<h3 className="account-order-detail-title">
										{t('account.shippingAddress')}
									</h3>
										<p className="account-order-detail-label">{data.name}</p>
										<div
											className="account-order-detail-value"
											dangerouslySetInnerHTML={{
												__html: data.shipping_address,
											}}
										/>
									</div>

									{/* Order History Timeline */}
									<div className="account-order-detail-section">
									<h3 className="account-order-detail-title">
										{t('account.orderHistoryTimeline')}
									</h3>
										{data.histories?.length > 0 ? (
											data.histories.map((h, index) => (
												<div
													key={index}
													className="account-order-timeline-item"
												>
													<div
														className={`account-order-timeline-dot ${
															index === data.histories.length - 1
																? 'account-order-timeline-dot-active'
																: ''
														}`}
													></div>
													<div className="account-order-timeline-content">
														<p className="account-order-timeline-status">
															{h.status}
														</p>
														<p className="account-order-timeline-date">
															{h.date_added}
														</p>
													</div>
												</div>
											))
										) : (
										<p className="account-order-detail-value">
											{t('account.historyEmpty')}
										</p>
										)}
									</div>
								</div>

								{/* Order Summary */}
								<div className="account-order-summary">
									{data.totals
										?.filter(t => t.code !== 'total')
										.map(t => (
											<div key={t.code} className="account-order-summary-row">
												<span className="account-order-summary-label">
													{t.title}
												</span>
												<span className="account-order-summary-value">
													{formatCurrency(t.text)}
												</span>
                  </div>
                ))}
									{data.totals?.find(t => t.code === 'total') && (
										<div className="account-order-summary-row account-order-summary-total">
										<span className="account-order-summary-label">{t('account.total')}</span>
											<span className="account-order-summary-value">
												{formatCurrency(
													data.totals.find(t => t.code === 'total').text
												)}
											</span>
										</div>
            )}
          </div>
                    </div>

							{/* Products in Order */}
							<h2 className="account-products-title">{t('account.productsInOrder')}</h2>
							<Row gutter={24} className="popular-products-list">
								{(data.products || []).map((item, index) => {
									const stock = getStock(item);
									const handleGotoItem = () => {
										if (item.product_id) {
											const slug = item.slug || item.name?.toLowerCase().replace(/\s+/g, '-') || 'product';
											smartNavigate(`/product/${item.product_id}/${slug}`);
										}
									};

									return (
										<Col
											xs={12}
											sm={12}
											md={12}
											lg={8}
											key={item.product_id || index}
										>
											<div
												className="popular-product-card"
												onClick={handleGotoItem}
												style={{ cursor: 'pointer' }}
											>
												<div className="popular-product-image-wrapper">
													<img
														src={item.thumb}
														alt={item.name}
														className="popular-product-image"
													/>
													{item.rating && (
														<div className="popular-product-rating">
															<Icon name="icon-star" className="popular-product-rating-icon" ariaLabel={t('home.rating')} />
															<span className="popular-product-rating-value">
																{item.rating}
															</span>
														</div>
            )}
												</div>
												<div
													className="popular-product-favorite"
													onClick={e => e.stopPropagation()}
												>
													<WishlistButton
														product_id={item.product_id}
														active={item.in_wishlist}
													/>
												</div>
												<h3 className="popular-product-title">
													{item.name}, {formatWeightWithUnit(item.weight)}
												</h3>
												<ProductPriceRow item={item} />
												<div onClick={e => e.stopPropagation()}>
													<CartQtyButtonGroup item={item} block size="large" />
												</div>
												<div
													className={`popular-product-stock ${
														stock.stock === 0
															? 'popular-product-stock-unavailable'
															: ''
													}`}
												>
											{stock.stock === 0
													? t('stock.outOfStock')
													: stock.stock === 999
													? t('stock.inStockMany')
													: t('stock.inStockCount', { count: stock.stock })}
												</div>
											</div>
										</Col>
									);
								})}
							</Row>
						</>
					) : (
						<div className="account-order-detail-card">
							<Skeleton active paragraph={{ rows: 10 }} />
						</div>
					)}
				</div>
			</div>

			{/* Кнопка выхода для мобильных */}
			<AccountLogoutButton />
    </div>
  );
};

export default HistoryDetail;
